/**
 * Zeigt eine versendete Erinnerungsmail so, wie der Kunde sie bekommen hat.
 *
 * Die 1. Erinnerung geht ohne Freigabe hinaus – also muss der Wortlaut im
 * Nachhinein kontrollierbar sein. Angezeigt wird deshalb ausschließlich, was
 * beim Versand mitgespeichert wurde: Betreff, Text und Signatur. Die Vorlage
 * wird nicht neu gerendert und die heute hinterlegte Signatur nicht gezogen –
 * sonst zeigte die Kontrolle einen Text, den der Kunde nie bekommen hat.
 *
 * Bei Einträgen aus der Zeit vor dieser Änderung fehlt der Wortlaut. Dann sagt
 * die Maske das, statt etwas zu rekonstruieren.
 */

import { Modal } from '../komponenten/Modal';
import type { Angebot, Erinnerung } from '../typen';
import './mailansicht.css';

export function MailAnsichtModal({
  mail,
  angebot,
  onSchliessen,
}: {
  mail: Erinnerung;
  angebot: Angebot;
  onSchliessen: () => void;
}) {
  const vorhanden = !!mail.betreff || !!mail.text;

  return (
    <Modal titel="Versendete Erinnerung" onSchliessen={onSchliessen} breite={720}>
      <div className="ma-kopf">
        <div className="ma-zeile">
          <span className="ma-feld">Von</span>
          <span className="ma-wert">{mail.user}</span>
        </div>
        <div className="ma-zeile">
          <span className="ma-feld">An</span>
          <span className="ma-wert">
            {angebot.kunde.name}
            {angebot.kunde.mail ? ` · ${angebot.kunde.mail}` : ' · keine Adresse hinterlegt'}
          </span>
        </div>
        <div className="ma-zeile">
          <span className="ma-feld">Betreff</span>
          <span className="ma-wert ma-betreff">{mail.betreff || '—'}</span>
        </div>
        <div className="ma-zeile">
          <span className="ma-feld">Versendet</span>
          <span className="ma-wert">
            {mail.wochentag}, {mail.datumText}
            {mail.zeit ? ` · ${mail.zeit}` : ''}
            {mail.trockenlauf && (
              <span className="ma-marke">Testbetrieb – als Entwurf abgelegt, nicht versendet</span>
            )}
          </span>
        </div>
        <div className="ma-zeile">
          <span className="ma-feld">Vorgang</span>
          <span className="ma-wert">
            {angebot.name} · {angebot.kategorie}
          </span>
        </div>
      </div>

      {vorhanden ? (
        <>
          <div className="ma-brief">
            <div className="ma-text">{mail.text}</div>
            {mail.signatur ? (
              <div className="ma-signatur" dangerouslySetInnerHTML={{ __html: mail.signatur }} />
            ) : (
              <div className="ma-ohne-signatur">
                Die Signatur wurde bei diesem Versand nicht mitgespeichert.
              </div>
            )}
          </div>
          <div className="ma-hinweis">
            Wortlaut wie versendet – nicht aus der heutigen Vorlage erzeugt.
          </div>
        </>
      ) : (
        <div className="ma-fehlt">
          Der Wortlaut dieser Mail wurde damals nicht mitgespeichert. Er lässt sich nicht
          zuverlässig rekonstruieren – die Vorlage kann sich seither geändert haben. Das Original
          liegt im Postfach des Versenders.
        </div>
      )}

      {mail.outlookWebLink && (
        <a className="ma-outlook" href={mail.outlookWebLink} target="_blank" rel="noreferrer">
          In Outlook öffnen ↗
        </a>
      )}
    </Modal>
  );
}